/** @forge-header
 * @id          ufcParser
 * @role        UFC ticker parser | KXUFCFIGHT fight parsing
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,TennisMatchInfo | kalshiTicker/helpers:parseFallback,formatTennisPlayerCode
 * @downstream  kalshiTicker/parse
 * @exports     parseUfcFight:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, TennisMatchInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, formatTennisPlayerCode } from '../helpers';

/**
 * UFC fight: KXUFCFIGHT-26JUL11MCGHOL-MCG
 * Structurally identical to a tennis match — 3+3 competitor codes concatenated
 * after the YYMONDD date, one ticker per fighter outcome. Reuses the
 * two-competitor `TennisMatchInfo` shape via the `fight` field.
 */
export function parseUfcFight(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) {
    return parseFallback(raw);
  }

  const mid = parts[1] ?? '';
  if (mid.length < 13) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const fightersStr = mid.slice(7);
  const half = Math.floor(fightersStr.length / 2);
  const outcome = parts[2] ?? null;

  const fight: TennisMatchInfo = {
    player1: {
      playerCode: fightersStr.slice(0, half),
      displayName: formatTennisPlayerCode(fightersStr.slice(0, half)),
    },
    player2: {
      playerCode: fightersStr.slice(half),
      displayName: formatTennisPlayerCode(fightersStr.slice(half)),
    },
  };

  const shortLabel = outcome
    ? `UFC: ${formatTennisPlayerCode(outcome)} Win`
    : 'UFC Fight';

  const fullLabel = `UFC: ${fight.player1.displayName} vs ${fight.player2.displayName}${outcome ? ` · ${formatTennisPlayerCode(outcome)} Win` : ''}`;

  return {
    raw,
    category: 'ufc-fight',
    series: 'KXUFCFIGHT',
    date,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    fight,
    outcome,
    shortLabel,
    fullLabel,
  };
}
